import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Popconfirm, Button as AntButton, Space, Tag } from 'antd'
import { DeleteOutlined, EditOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import btnStyles from '../../styles/Button.module.css'
import styles from '../../styles/Admin.module.css'
import useFetchOwners from '../../hooks/useFetchOwners'
import { axiosReq } from '../../api/axiosDefaults'
import { useTranslation } from 'react-i18next'

export const StaffCard = ({ handleDelete, handleEdit, ...props }) => {
    const [show, setShow] = useState(false);
    const { t } = useTranslation();
    const { owners, setOwners } = useFetchOwners();
    const owner = owners.find(o => o.id === props?.listing_owner);

    const handleDeleteFile = async (fileId) => {
        try {
            await axiosReq.delete(`/owners/${owner.id}/files/${fileId}/`);
            setOwners(owners.map(o =>
                o.id === owner.id
                    ? { ...o, files: o.files.filter(f => f.id !== fileId) }
                    : o
            ));
        } catch (error) {
            console.error("Error deleting file:", error.response?.data || error.message);
        }
    };

    return (
        <>
            {/* Owner info */}
            <Col sm={12} className="mb-3">
                <Card className="h-100 shadow-sm">
                    <Card.Header style={{ backgroundColor: '#847c3d', color: '#fff', fontWeight: 600 }}>
                        <UserOutlined className="me-2" />
                        {t('propertyDetails.owner')}
                    </Card.Header>
                    <Card.Body>
                        <Card.Text>
                            <strong>{t('propertyDetails.owner')}:</strong>{' '}
                            <Link to={`/frontend/admin/listings/owners/${props.listing_owner}`}>
                                {owner?.first_name} {owner?.last_name}
                            </Link>
                        </Card.Text>
                        <Card.Text>
                            <strong>{t('admin.owner.phone')}:</strong> {owner?.phone}
                        </Card.Text>
                        {owner?.phone_2 && (
                            <Card.Text>
                                <strong>{t('admin.owner.phone2')}:</strong> {owner.phone_2}
                            </Card.Text>
                        )}
                        <Card.Text>
                            <strong>{t('admin.owner.email')}:</strong>{' '}
                            <a href={`mailto:${owner?.email}`}>{owner?.email}</a>
                        </Card.Text>
                        {owner?.notes && (
                            <Card.Text>
                                <strong>{t('admin.owner.notes')}:</strong> {owner.notes}
                            </Card.Text>
                        )}

                        <h6 className="mt-3 mb-2">{t('admin.owner.uploadedFiles')}</h6>
                        <ul className={`list-unstyled ${styles.OwnerList}`}>
                            {owner?.files?.length > 0 ? (
                                owner.files.map((file, index) => (
                                    <li key={index} className="border rounded shadow p-1 mb-2 d-flex align-items-center" style={{ maxWidth: '100%' }}>
                                        <a href={file.file} target="_blank" rel="noopener noreferrer" className="text-truncate me-auto">
                                            {file.file_url}
                                        </a>
                                        <Popconfirm
                                            title={t('admin.fileSelection.deleteFileConfirmation')}
                                            onConfirm={() => handleDeleteFile(file.id)}
                                            okText={t('admin.fileSelection.yesDelete')}
                                            cancelText={t('admin.fileSelection.noCancel')}
                                            okButtonProps={{ danger: true }}
                                        >
                                            <AntButton
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                style={{ marginLeft: '8px', flexShrink: 0 }}
                                            />
                                        </Popconfirm>
                                    </li>
                                ))
                            ) : (
                                <li className="text-muted">{t('admin.owner.noFilesUploaded')}</li>
                            )}
                        </ul>
                    </Card.Body>
                </Card>
            </Col>

            {/* Agent + metadata */}
            <Col sm={12} className="mb-3">
                <Card className="h-100 shadow-sm">
                    <Card.Header style={{ backgroundColor: '#847c3d', color: '#fff', fontWeight: 600 }}>
                        <CalendarOutlined className="me-2" />
                        {t('propertyDetails.agentName')}
                    </Card.Header>
                    <Card.Body>
                        <Card.Text>
                            <strong>{t('propertyDetails.agentName')}:</strong>{' '}
                            <Link to={`/profiles/${props.profile_id}`} className="text-decoration-none">
                                {props.agent_name}
                            </Link>
                        </Card.Text>
                        <Card.Text>
                            <strong>{t('admin.listingsForms.createdOn')}:</strong> {props.created_on}
                        </Card.Text>
                        <Card.Text>
                            <strong>{t('admin.listingsForms.updatedOn')}:</strong> {props.updated_on}
                        </Card.Text>
                        <Space className="mt-2">
                            {props.approved && <Tag color="green">{t('propertyDetails.status.approved')}</Tag>}
                            {props.featured && <Tag color="gold">{t('propertyDetails.status.featured')}</Tag>}
                            {!props.approved && <Tag color="default">{t('propertyDetails.status.pending')}</Tag>}
                        </Space>
                    </Card.Body>
                </Card>
            </Col>

            {/* Action buttons */}
            <Col sm={12} className="mb-3">
                <Space>
                    <Button className={btnStyles.AngryOcean} onClick={handleEdit}>
                        <EditOutlined className="me-1" />
                        {t('admin.listingsForms.editListing')}
                    </Button>
                    <Button className={btnStyles.Remove} onClick={() => setShow(true)}>
                        <DeleteOutlined className="me-1" />
                        {t('admin.listingsForms.deleteListing')}
                    </Button>
                </Space>
            </Col>

            {/* Delete confirmation modal */}
            <Modal show={show} onHide={() => setShow(false)} centered size="md" className="text-center">
                <Modal.Header closeButton className="text-dark border-dark" style={{ backgroundColor: 'rgba(132, 124, 61, 0.85)' }}>
                    <Modal.Title>{t('admin.listingsForms.deleteListing')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-dark" style={{ backgroundColor: '#847c3d' }}>
                    <p className="h4">{t('admin.listingsForms.deleteListingConfirm')}</p>
                </Modal.Body>
                <Modal.Footer className="text-dark d-flex justify-content-center border-dark" style={{ backgroundColor: 'rgba(132, 124, 61, 0.85)' }}>
                    <Button className={`${btnStyles.AngryOcean} ${btnStyles.Button}`} onClick={() => setShow(false)}>
                        {t('admin.owner.close')}
                    </Button>
                    <Button className={`${btnStyles.Remove} ${btnStyles.Button}`} onClick={handleDelete}>
                        {t('admin.listingsForms.deleteListing')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
